import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Sparkles, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: '', description: '', price: '' };

export const ProviderAddOns = () => {
  const { t } = useTranslation();
  const token = localStorage.getItem('sessionToken');
  const [addOns, setAddOns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/provider/add-ons`, { headers: { Authorization: `Bearer ${token}` } });
      setAddOns(res.data.add_ons || []);
    } catch (e) {
      setError(t('addOns.loadFailed', 'Could not load your add-ons'));
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setError('');
    if (!form.name.trim()) {
      setError(t('addOns.nameRequired', 'Give the add-on a name'));
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/provider/add-ons`, {
        name: form.name.trim(),
        description: form.description.trim() || null,
        price: Number(form.price || 0),
      }, { headers });
      setForm(EMPTY);
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(detail?.message || (typeof detail === 'string' ? detail : t('addOns.saveFailed', 'Could not save the add-on')));
    } finally {
      setSaving(false);
    }
  };

  const patch = async (addon, updates) => {
    setAddOns((list) => list.map((a) => (a.id === addon.id ? { ...a, ...updates } : a)));
    try {
      await axios.put(`${API}/provider/add-ons/${addon.id}`, updates, { headers });
    } catch (e) {
      setError(t('addOns.saveFailed', 'Could not save the add-on'));
      load();
    }
  };

  const remove = async (addon) => {
    if (!window.confirm(t('addOns.removeConfirm', 'Remove "{{name}}"? Past bookings keep it on their bill.', { name: addon.name }))) return;
    try {
      await axios.delete(`${API}/provider/add-ons/${addon.id}`, { headers });
      setAddOns((list) => list.filter((a) => a.id !== addon.id));
    } catch (e) {
      setError(t('addOns.removeFailed', 'Could not remove the add-on'));
    }
  };

  const field = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200';

  return (
    <div className="mt-8 border-t border-gray-200 pt-6" data-testid="provider-addons">
      <div className="mb-1 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-amber-500" />
        <h3 className="text-lg font-semibold text-gray-900">{t('addOns.providerTitle', 'Add-ons')}</h3>
        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700">
          {t('addOns.noTime', 'No extra time')}
        </span>
      </div>
      <p className="mb-5 text-sm text-gray-500">
        {t('addOns.providerSubtitle', 'Paid extras like a face mask or hot towel. Customers tick them at checkout and they go straight onto the bill without changing the appointment length.')}
      </p>

      {error && (
        <p className="mb-3 rounded-lg bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700" data-testid="addons-error">{error}</p>
      )}

      <div className="mb-5 grid gap-3 rounded-xl bg-gray-50 p-4 sm:grid-cols-[1.2fr_1.6fr_0.7fr_auto]">
        <input
          className={field}
          placeholder={t('addOns.namePlaceholder', 'e.g. Hot towel')}
          value={form.name}
          data-testid="addon-name-input"
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <input
          className={field}
          placeholder={t('addOns.descriptionPlaceholder', 'Short description (optional)')}
          value={form.description}
          data-testid="addon-description-input"
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
        <input
          className={`${field} numeric-ltr`}
          type="number"
          min="0"
          step="0.25"
          placeholder={t('common.currency', 'KWD')}
          value={form.price}
          data-testid="addon-price-input"
          onChange={(e) => setForm({ ...form, price: e.target.value })}
        />
        <button
          onClick={create}
          disabled={saving}
          data-testid="addon-create-btn"
          className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          {t('addOns.add', 'Add')}
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">{t('common.loading', 'Loading...')}</p>
      ) : addOns.length === 0 ? (
        <p className="rounded-xl border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500" data-testid="addons-empty">
          {t('addOns.emptyProvider', 'No add-ons yet. Face mask, hot towel, exfoliation — anything you sell on the spot.')}
        </p>
      ) : (
        <div className="grid gap-3" data-testid="addons-list">
          {addOns.map((addon) => (
            <div
              key={addon.id}
              data-testid="addon-row"
              className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border p-4 ${addon.active ? 'border-gray-200' : 'border-gray-200 bg-gray-50 opacity-70'}`}
            >
              <div className="min-w-[180px] flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-900">{addon.name}</span>
                  {!addon.active && (
                    <span className="rounded bg-gray-200 px-2 py-0.5 text-[11px] font-bold text-gray-600">
                      {t('addOns.hidden', 'Hidden')}
                    </span>
                  )}
                </div>
                {addon.description && <p className="mt-0.5 text-sm text-gray-500">{addon.description}</p>}
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <input
                    type="number"
                    min="0"
                    step="0.25"
                    defaultValue={addon.price}
                    data-testid="addon-price-edit"
                    onBlur={(e) => {
                      const price = Number(e.target.value);
                      if (!Number.isNaN(price) && price !== addon.price) patch(addon, { price });
                    }}
                    className="numeric-ltr w-20 rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
                  />
                  <span className="text-xs font-semibold text-gray-500">{t('common.currency', 'KWD')}</span>
                </div>
                <label className="flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-gray-600">
                  <input
                    type="checkbox"
                    checked={addon.active}
                    data-testid="addon-active-toggle"
                    onChange={(e) => patch(addon, { active: e.target.checked })}
                  />
                  {t('addOns.offered', 'Offered')}
                </label>
                <button
                  onClick={() => remove(addon)}
                  data-testid="addon-remove-btn"
                  className="rounded-lg p-2 text-rose-600 hover:bg-rose-50"
                  aria-label={t('addOns.remove', 'Remove')}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProviderAddOns;
